import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { invalidate } from 'queries/invalidate';
import Select from 'components/Inputs/Select';
import BasicModal from 'components/Modals/presets/BasicModal';
import { isStandardObject } from 'components/Modals/utilities';
import FieldService from 'services/FieldService';
import Loader from 'components/Kizen/Loader';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';
import { FIELD_TYPES } from 'utility/constants';
import { KeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useKeyListeners } from 'hooks/keyboardEventHandler/useKeyListeners';
import { toastVariant, useToast } from 'components/ToastProvider';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { RelationshipConfirmationModal } from 'components/Modals/RelationshipConfirmationModal';
import {
  CO_RELATION_TYPE_ONE_TO_ONE,
  CO_RELATION_TYPE_PRIMARY_REVERSE,
} from 'components/Modals/utilities';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import CreateEntityModalFull, {
  PIPELINE,
  CUSTOM_OBJECT,
  CONTACT,
} from 'components/Modals/CreateEntity/FullForm.jsx';
import { useQuery, useQueries } from '@tanstack/react-query';
import { pipelineObjectFieldsQuery } from 'query/pipeline';
import {
  customObjectFieldsQuery,
  customObjectModelQuery,
} from 'query/custom-object';

const defaultPipeline = {
  id: null,
  name: '',
  objectId: null,
  relationField: null,
  entityValue: null,
  stage: null,
  estimatedCloseDate: null,
};

const disableSave = ({ objectId, relationField }) => {
  return objectId === null || relationField === null;
};

const PipelineModal = ({
  id,
  entityId,
  objectId,
  pipelineOptions,
  clientObject,
  onAdd,
  onCancel,
  updateData,
  fieldState,
  relationshipFields,
  handleFieldBlur,
  handleUpdateRecord,
  show: showFirstStepModal,
  setShowModal: setShowFirstStepModal,
  entitiyLabel,
  entityPlaceHolder,
  refetch,
}) => {
  const { t } = useTranslation();
  const defaultPipelineRef = useRef(true);
  const objectIdRef = useRef(objectId);

  const [showCreateCustomObjectModal, setShowCreateCustomObjectModal] =
    useState(false);
  const [objectIdToFetch, setObjectIdToFetch] = useState(null);
  const [pipeline, setPipeline] = useState(defaultPipeline);
  const [showToast] = useToast();
  const [relationshipConfirmationModal, setRelationshipConfirmationModal] =
    useToggle(false);

  const { data: customObject, isLoading: recordFieldsIsLoading } = useQuery(
    customObjectModelQuery(objectIdToFetch, {
      enabled: !!objectIdToFetch && showFirstStepModal,
    })
  );

  const isPipeline = customObject?.fetch_url === 'pipeline';
  const shouldFetchFields = !!customObject?.id && showFirstStepModal;
  const fieldsQuery = useQueries({
    queries: [
      pipelineObjectFieldsQuery(customObject?.id, {
        enabled: isPipeline && shouldFetchFields,
      }),
      customObjectFieldsQuery(customObject?.id, {
        enabled: !isPipeline && shouldFetchFields,
      }),
    ],
    combine: ([pipeline, custom]) => {
      return isPipeline ? pipeline : custom;
    },
  });

  const { data: fields, isLoading: fieldsLoading } = fieldsQuery;

  const handlePipelineChange = useCallback(
    async ({ value }) => {
      setPipeline({
        ...pipeline,
        objectId: value,
        relationField: null,
        stage: null,
      });
      setObjectIdToFetch(value);
    },
    [pipeline]
  );

  const [showChangesConfirmation, setShowChangesConfirmation] = useState(false);

  const onHideCanceled = () => setShowChangesConfirmation(false);

  const onHideConfirmed = () => {
    setPipeline(defaultPipeline);
    setShowChangesConfirmation(false);
    setShowFirstStepModal(false, true);
    onCancel?.();
  };

  const handleOnHide = () => {
    defaultPipelineRef.current = true;
    if (pipeline.objectId) {
      setShowChangesConfirmation(true);
    } else {
      onHideConfirmed();
    }
  };

  const handleUpdateEntityRecord = useCallback(
    async (data) => {
      try {
        const fieldId = pipeline?.relationField?.value;
        let relationshipFieldIds = [];

        if (fieldId) {
          const fieldValues = await FieldService.getFieldValues(
            entityId,
            fieldId
          );
          relationshipFieldIds = Array.isArray(fieldValues?.results)
            ? fieldValues.results.map(({ id }) => id)
            : [];
        }

        const stagedFieldValues = Array.isArray(
          fieldState?.[pipeline?.relationField?.value]
        )
          ? fieldState[pipeline.relationField.value].map((field) => field.id)
          : [];

        const payload = {
          fields: [
            {
              id: pipeline.relationField?.value,
              value: [
                CO_RELATION_TYPE_PRIMARY_REVERSE,
                CO_RELATION_TYPE_ONE_TO_ONE,
              ].includes(pipeline.relationField?.field?.relation?.relation_type)
                ? [data?.id]
                : [...relationshipFieldIds, ...stagedFieldValues, data?.id],
            },
          ],
        };
        const isClient = clientObject?.id === objectId;
        if (isClient) {
          await FieldService.updateObject(
            'contacts',
            entityId ? entityId : id,
            payload
          );
        } else {
          await handleUpdateRecord(payload);
        }
        invalidate.TIMELINE.ALL();
      } catch (error) {
        const toastMessage = t(
          'The related record was created, but there was an error relating it to this record.'
        );
        setTimeout(() =>
          showToast({
            variant: toastVariant.FAILURE,
            message: toastMessage,
          })
        );
      }
    },
    [
      entityId,
      pipeline,
      fieldState,
      handleUpdateRecord,
      showToast,
      clientObject?.id,
      objectId,
      id,
      t,
    ]
  );

  const handleCreateEntityRecord = useCallback(
    async (newObject) => {
      await handleUpdateEntityRecord(newObject);
      refetch();
      setPipeline(defaultPipeline);
      onAdd(newObject);
      updateData?.();
      handleFieldBlur?.();
      setShowFirstStepModal(false);
      defaultPipelineRef.current = true;
    },

    [
      handleUpdateEntityRecord,
      handleFieldBlur,
      onAdd,
      refetch,
      updateData,
      setShowFirstStepModal,
    ]
  );

  const handleFirstStepConfirm = useCallback(async () => {
    if (
      [CO_RELATION_TYPE_PRIMARY_REVERSE, CO_RELATION_TYPE_ONE_TO_ONE].includes(
        pipeline.relationField.field.relation.relation_type
      ) &&
      fieldState?.[pipeline.relationField.value]
    ) {
      setRelationshipConfirmationModal();
    } else {
      setShowCreateCustomObjectModal(true);
      setShowFirstStepModal(false);
    }
  }, [
    setShowFirstStepModal,
    setRelationshipConfirmationModal,
    fieldState,
    pipeline,
  ]);

  const options = useMemo(() => {
    if (
      !fields?.length ||
      pipelineOptions.length === 0 ||
      !showFirstStepModal
    ) {
      return EMPTY_ARRAY;
    }

    return fields
      .filter((field) => {
        const relationshipFieldsIds = relationshipFields?.[customObject?.id]
          ? relationshipFields[customObject?.id].map((field) => field.value)
          : [];

        // if no relationship fields are selected, show all fields
        if (!field.relation) {
          return false;
        }
        return (
          (field.relation?.related_object === objectId &&
            relationshipFieldsIds.length === 0) ||
          relationshipFieldsIds.includes(field.relation?.related_field)
        );
      })
      .reduce((acc, field) => {
        if (field.relation) {
          acc.push({
            value: field.relation.related_field,
            label: field.relation.related_name,
            field,
          });
        }
        return acc;
      }, []);
  }, [
    fields,
    pipelineOptions,
    objectId,
    relationshipFields,
    customObject,
    showFirstStepModal,
  ]);

  useEffect(() => {
    if (options.length === 1) {
      setPipeline((prev) => ({ ...prev, relationField: options[0] }));
    }
  }, [options, setPipeline]);

  useEffect(() => {
    if (objectIdRef.current !== objectId) {
      defaultPipelineRef.current = true;
    }
  }, [objectId]);

  useEffect(() => {
    if (
      (pipelineOptions.length === 1 && defaultPipelineRef.current) ||
      (pipeline.objectId !== pipelineOptions[0]?.value &&
        pipelineOptions.length === 1)
    ) {
      defaultPipelineRef.current = false;
      handlePipelineChange(pipelineOptions[0]);
    }
  }, [objectId, pipelineOptions, handlePipelineChange, pipeline.objectId]);

  const { assignFieldHandle, getKeyListenersProps } = useKeyListeners(
    [
      ...(pipeline.objectId
        ? [
            { id: 'workflow-pipeline' },
            { id: 'relationship-to-new' },
            customObject?.trackEntityValue
              ? { id: 'track-entity-value' }
              : null,
          ]
        : []),
    ].filter(Boolean)
  );

  const handleConfirmRelationship = useCallback(() => {
    setRelationshipConfirmationModal();
    setShowCreateCustomObjectModal(true);
    setShowFirstStepModal(false);
  }, [
    setRelationshipConfirmationModal,
    setShowFirstStepModal,
    setShowCreateCustomObjectModal,
  ]);

  const handleHideConfirmRelationship = useCallback(() => {
    setRelationshipConfirmationModal();
  }, [setRelationshipConfirmationModal]);

  const objectType =
    customObject?.fetchUrl === 'client'
      ? CONTACT
      : isStandardObject(customObject ?? {})
        ? CUSTOM_OBJECT
        : PIPELINE;

  return (
    <>
      <BasicModal
        onHide={handleOnHide}
        onConfirm={handleFirstStepConfirm}
        show={showFirstStepModal}
        actionBtnColor="blue"
        buttonText={t('Continue')}
        defaultLeftBtnText={t('Cancel')}
        heading={t('Add Related Entity')}
        disabled={disableSave(pipeline)}
        size="small"
      >
        <KeyBoardContext.Provider value={{ assignFieldHandle }}>
          <Select
            label={t('{{entitiyLabel}}', {
              entitiyLabel: entitiyLabel ?? t('Workflow/Pipeline*'),
            })}
            placeholder={t('{{entityPlaceHolder}}', {
              entityPlaceHolder:
                entityPlaceHolder ?? t('Choose Workflow/Pipeline'),
            })}
            options={pipelineOptions}
            value={pipeline.objectId}
            onChange={handlePipelineChange}
            disabled={pipelineOptions.length === 1}
            inModal
            menuInline
            margin
            {...getKeyListenersProps('workflow-pipeline')}
          />
          {pipeline.objectId && customObject ? (
            <>
              <Select
                label={`${t('Field to Create New Entity In (This Object)')}*`}
                placeholder={t('Choose Relationship Field')}
                options={options}
                value={pipeline.relationField}
                isGetFullObject
                onChange={(val) =>
                  setPipeline({ ...pipeline, relationField: val })
                }
                inModal
                menuInline
                margin
                field={{
                  fieldType: FIELD_TYPES.Dropdown,
                  options: options,
                }}
                {...getKeyListenersProps('relationship-to-new')}
              />
            </>
          ) : (
            <Loader loading={recordFieldsIsLoading || fieldsLoading} />
          )}
        </KeyBoardContext.Provider>
      </BasicModal>
      <RelationshipConfirmationModal
        show={relationshipConfirmationModal}
        onConfirm={handleConfirmRelationship}
        onHide={handleHideConfirmRelationship}
      />
      <CreateEntityModalFull
        callRecordsEndpoint
        objectType={objectType}
        objectId={customObject?.id}
        show={showCreateCustomObjectModal}
        onCreated={handleCreateEntityRecord}
        defaultDirty
        onCancel={() => {
          defaultPipelineRef.current = true;
          setPipeline(defaultPipeline);
          setShowCreateCustomObjectModal(false);
          onCancel?.();
        }}
      />
      <ConfirmationModal
        heading={t('You Have Unsaved Changes')}
        buttonText={t('Discard Changes')}
        defaultLeftBtnText={t('Cancel')}
        actionBtnColor="red"
        show={showChangesConfirmation}
        onConfirm={onHideConfirmed}
        onHide={onHideCanceled}
      >
        {t('Unsaved changes will be lost, would you like to continue?')}
      </ConfirmationModal>
    </>
  );
};

export default PipelineModal;
